import React from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { Button, Card, Icon } from '../../../components/common';
import { getAttendanceRequiredActorIdsForEvent } from '../../attendance/required-actors';
import { getLocalDateKey } from '../logic/date-keys';

interface AttendanceSummary {
    totalCount: number;
    presentCount: number;
    lateCount: number;
    excusedCount: number;
    unexcusedCount: number;
    missingCount: number;
}

export const AttendanceWidget: React.FC = () => {
    const attendanceRecords = useAppStore(state => state.attendanceRecords);
    const events = useAppStore(state => state.rehearsalSchedule || []);
    const personnel = useAppStore(state => state.personnel || []);
    const showStructure = useAppStore(state => state.showStructure);
    const rehearsalScenes = useAppStore(state => state.rehearsalScenes || []);
    const castAssignments = useAppStore(state => state.castAssignments || []);
    const setActiveTab = useAppStore(state => state.metaActions.setActiveTab);

    const today = getLocalDateKey();
    const todayRecords = attendanceRecords.filter(record => record.eventDate === today);
    const todayRecordsByEventId = new Map(todayRecords.map(record => [record.eventId, record]));
    const todayEvents = events.filter(event => event.date === today);
    const activeCast = personnel.filter(member => member.category === 'cast' && !member.deleted);

    const summary = todayEvents.reduce<AttendanceSummary>((acc, event) => {
        const requiredActorIds = getAttendanceRequiredActorIdsForEvent(event, {
            actors: activeCast,
            personnel,
            characters: showStructure.characters || [],
            acts: showStructure.acts || [],
            rehearsalScenes,
            castAssignments,
        });
        const record = todayRecordsByEventId.get(event.id);

        for (const actorId of requiredActorIds) {
            acc.totalCount += 1;
            const status = record?.actors[actorId]?.status ?? 'absent';

            if (status === 'present') {
                acc.presentCount += 1;
            } else if (status === 'late') {
                acc.lateCount += 1;
            } else if (status === 'excused') {
                acc.excusedCount += 1;
            } else if (status === 'unexcused') {
                acc.unexcusedCount += 1;
            } else {
                acc.missingCount += 1;
            }
        }

        return acc;
    }, {
        totalCount: 0,
        presentCount: 0,
        lateCount: 0,
        excusedCount: 0,
        unexcusedCount: 0,
        missingCount: 0,
    });

    const { totalCount, presentCount, lateCount, excusedCount, unexcusedCount, missingCount } = summary;
    const total = totalCount || 1;
    const accountedCount = presentCount + lateCount + excusedCount + unexcusedCount;
    const presentRatio = accountedCount / total;

    if (todayEvents.length === 0) {
        return (
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Icon name="groups" className="text-primary" />
                    Today&apos;s Attendance
                </h3>
                <div className="text-center py-6">
                    <Icon name="event_busy" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No events today</p>
                </div>
            </div>
        );
    }

    if (totalCount === 0) {
        return (
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Icon name="groups" className="text-primary" />
                    Today&apos;s Attendance
                </h3>
                <div className="text-center py-6">
                    <Icon name="person_off" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No actors called for today&apos;s events</p>
                </div>
            </div>
        );
    }

    return (
        <Card
            variant="outlined"
            padding="none"
            className="w-full p-6 text-left"
        >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Icon name="groups" className="text-primary" />
                Today&apos;s Attendance
            </h3>

            <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    {presentCount}/{totalCount} Present
                </span>
                <span aria-hidden="true">/</span>
                {lateCount > 0 ? (
                    <>
                        <span className="text-amber-600 dark:text-amber-400">{lateCount} Late</span>
                        <span aria-hidden="true">/</span>
                    </>
                ) : null}
                {excusedCount > 0 ? (
                    <>
                        <span className="text-gray-500 dark:text-gray-400">{excusedCount} Excused</span>
                        <span aria-hidden="true">/</span>
                    </>
                ) : null}
                {unexcusedCount > 0 ? (
                    <>
                        <span className="text-red-600 dark:text-red-400">{unexcusedCount} Unexcused</span>
                        <span aria-hidden="true">/</span>
                    </>
                ) : null}
                {missingCount > 0 ? (
                    <span className="text-red-600 dark:text-red-400 font-semibold">
                        {missingCount} Missing
                    </span>
                ) : null}
                {missingCount === 0 ? (
                    <span className="text-emerald-600 dark:text-emerald-400">All accounted for</span>
                ) : null}
            </div>

            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.round(presentRatio * 100)}%` }}
                />
            </div>

            <Button
                variant="secondary"
                size="sm"
                onClick={() => setActiveTab('personnel')}
                className="mt-3"
                rightIcon="arrow_forward"
            >
                View attendance details
            </Button>
        </Card>
    );
};
